import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import { invokeFunction } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { LearnerProfile, WorkspaceCourse } from "@/lib/learnerHub/types";
import { getLearnerUnitMap } from "@/lib/learnerHub/utils";
import { ROLE_PROFILES, unitById } from "@/lib/learnerHub/constants";

interface Props {
  learner: LearnerProfile;
  courses: WorkspaceCourse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLearnerUpdate: (updated: LearnerProfile) => void;
}

interface AISuggestion {
  courseTitle: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  reason: string;
}

interface AIResult {
  suggestions: AISuggestion[];
  summary: string;
}

function priorityClass(priority: string) {
  if (priority === "HIGH") return "bg-green-100 text-green-800 border-green-200";
  if (priority === "MEDIUM") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-muted text-muted-foreground";
}

export default function AssignCourseDialog({
  learner, courses, open, onOpenChange, onLearnerUpdate,
}: Props) {
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [email, setEmail] = useState(learner.email || "");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AIResult | null>(null);
  const [assigning, setAssigning] = useState(false);

  const unitMap = getLearnerUnitMap(learner);
  const requiredUnits = ROLE_PROFILES[learner.role] || [];
  const gappedUnits = requiredUnits
    .filter((uid) => !unitMap[uid])
    .map((uid) => ({ id: uid, label: unitById(uid)?.label || uid }));

  const getAISuggestions = async () => {
    if (!courses.length) return;
    setAiLoading(true);
    setAiResult(null);
    try {
      const coveredLines = Object.entries(unitMap).map(([uid, hit]) => {
        const unit = unitById(uid);
        return `- ${uid}: ${unit?.label || uid} (via ${hit.courseName})`;
      });
      const gapLines = gappedUnits.map((u) => `- ${u.id}: ${u.label}`);
      const courseList = courses
        .map((c) => `- "${c.title}"${c.description ? `: ${c.description}` : ""}`)
        .join("\n");

      const prompt = `You are a professional development advisor for a mining training company.

Learner profile:
- Name: ${learner.name}
- Role: ${learner.role}
- Site: ${learner.site}

Competency gaps (required for this role, not yet covered):
${gapLines.length ? gapLines.join("\n") : "None — all role requirements are met"}

Currently covered competencies:
${coveredLines.length ? coveredLines.join("\n") : "None yet"}

Available courses in this workspace:
${courseList}

Based on the course titles and the learner's competency gaps, suggest which course(s) would most benefit this learner's professional development. Prioritise filling critical gaps for their current role, then consider broader capability building.

Return ONLY valid JSON:
{
  "suggestions": [
    {
      "courseTitle": "exact course title from the list above",
      "priority": "HIGH",
      "reason": "one sentence explaining why this course addresses the learner's specific needs"
    }
  ],
  "summary": "one concise sentence overall recommendation"
}`;

      const { data, error } = await invokeFunction("hub-assistant", {
        body: { prompt, maxTokens: 800 },
      });
      if (error) throw new Error(typeof error === "string" ? error : (error as any).message);
      const parsed: AIResult = JSON.parse((data as any).text || "{}");
      setAiResult(parsed);
      // Auto-select the top suggestion if nothing is selected
      if (!selectedCourseId && parsed.suggestions?.length) {
        const match = courses.find((c) => c.title === parsed.suggestions[0].courseTitle);
        if (match) setSelectedCourseId(match.id);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to get AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCourseId) { toast.error("Select a course first"); return; }
    const effectiveEmail = email.trim();
    if (!effectiveEmail || !effectiveEmail.includes("@")) {
      toast.error("A valid email address is required to enroll the learner");
      return;
    }
    setAssigning(true);
    try {
      const { data, error } = await invokeFunction("add-course-students", {
        body: { courseId: selectedCourseId, emails: [effectiveEmail] },
      });
      if (error) throw new Error(typeof error === "string" ? error : (error as any).message || "Enrollment failed");

      const { added, invited, alreadyEnrolled } = (data as any) || {};
      const courseName = courses.find((c) => c.id === selectedCourseId)?.title || "course";

      if (added?.length || invited?.length) {
        if (!learner.email && effectiveEmail) {
          onLearnerUpdate({ ...learner, email: effectiveEmail });
        }
        toast.success(
          `${learner.name} ${invited?.length ? "invited to" : "enrolled in"} "${courseName}"`
        );
        onOpenChange(false);
      } else if (alreadyEnrolled?.length) {
        toast.info(`${effectiveEmail} is already enrolled in "${courseName}"`);
      } else {
        toast.error("Could not enroll — check the email has an account");
      }
    } catch (err: any) {
      toast.error(err.message || "Enrollment failed");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign {learner.name} to a course</DialogTitle>
          <DialogDescription>{learner.role} · {learner.site}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Gap summary */}
          {gappedUnits.length > 0 ? (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wide">
                Role gaps — {gappedUnits.length} uncovered unit{gappedUnits.length !== 1 ? "s" : ""}
              </p>
              <div className="flex flex-wrap gap-1">
                {gappedUnits.map((u) => (
                  <Badge key={u.id} variant="outline" className="text-xs font-mono" title={u.label}>
                    {u.id}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              All role requirements covered — assigning for broader development.
            </div>
          )}

          {/* AI suggestions */}
          {courses.length > 0 && (
            <div className="space-y-3">
              <Button
                variant="outline"
                size="sm"
                onClick={getAISuggestions}
                disabled={aiLoading}
                className="w-full"
              >
                {aiLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Getting suggestions…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" />Suggest courses with AI</>
                )}
              </Button>

              {aiResult && (
                <div className="space-y-2">
                  {aiResult.summary && (
                    <p className="text-xs text-muted-foreground italic">{aiResult.summary}</p>
                  )}
                  {aiResult.suggestions?.map((s) => {
                    const course = courses.find((c) => c.title === s.courseTitle);
                    const isSelected = course && selectedCourseId === course.id;
                    return (
                      <div
                        key={s.courseTitle}
                        role="button"
                        tabIndex={0}
                        className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/40"
                        }`}
                        onClick={() => course && setSelectedCourseId(course.id)}
                        onKeyDown={(e) => e.key === "Enter" && course && setSelectedCourseId(course.id)}
                      >
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-sm font-medium">{s.courseTitle}</span>
                          <Badge
                            variant="outline"
                            className={`text-xs shrink-0 ${priorityClass(s.priority)}`}
                          >
                            {s.priority}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{s.reason}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Course selector */}
          {courses.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 shrink-0" />
              No courses in this workspace yet.
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs">Course</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
              >
                <option value="">— select a course —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.title}</option>
                ))}
              </select>
            </div>
          )}

          {/* Email */}
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1">
              Learner email
              {!learner.email && (
                <span className="text-amber-500 font-normal">(required for enrollment)</span>
              )}
            </Label>
            <Input
              type="email"
              placeholder="learner@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-8 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              The learner needs a Distilled Intuition account — they'll be enrolled or sent an invite.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAssign} disabled={!selectedCourseId || assigning}>
            {assigning
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Assigning…</>
              : "Assign to course"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
